/**
 * jQuery functions to get and set HTML5-compliant Boolean Attributes
 * https://www.w3.org/TR/html5/infrastructure.html#sec-boolean-attributes
 *
 * The presence of a boolean attribute on an element represents the true value,
 * and the absence of the attribute represents the false value.
 *
 * If the attribute is present, its value must either be the empty string
 * or a value that is an ASCII case-insensitive match for the
 * attribute’s canonical name, with no leading or trailing whitespace.
 */

const _ = require('lodash')

const propAttrs = [
  'required',
  'disabled',
  'checked',
  'readonly'
]

// Public functions exposed on the jQuery object

/**
 * boolAttr
 * Works like $el.attr(): http://api.jquery.com/attr/
 *
 * Getter
 * > $el.boolAttr( attributeName )
 * < Boolean
 *
 * Setter
 * > $el.boolAttr( attributeName, bool )
 * < $el
 * > $el.boolAttr( { attributeName: bool [, ... ] } )
 * < $el
 */
$.fn.boolAttr = function (attr, bool) {
  if (_.isObject(attr)) {
    // Handle object syntax for setting multiple attributes
    return boolAttributesObject.call(this, attr)
  }
  if (attr === 'value') {
    // Encapsulate surprising behavior with `value` attribute
    return boolValueAttribute.call(this, bool)
  }
  if (propAttrs.includes(attr)) {
    // Prevent suprising behavior with attrs that become props
    return boolProperty.call(this, attr, bool)
  }
  // Handle attribute according to Boolean Attributes spec
  return boolAttribute.call(this, attr, bool)
}

/**
 * toggleAttr
 * Works like $el.toggleClass: http://api.jquery.com/toggleClass/
 *
 * Setter
 * > $el.toggleAttr( attributeName [, state ])
 * < $el
 */
$.fn.toggleAttr = function (attr, state) {
  // Explicit boolean state setter
  if (typeof state !== 'undefined') {
    return this.boolAttr(attr, state)
  }
  // Toggle boolean based on current state
  return this.each(function () {
    const $this = $(this)
    $this.boolAttr(attr, !$this.boolAttr(attr))
  })
}

/**
 * boolData
 * Works like $el.data(): http://api.jquery.com/data/
 *
 * Getter
 * > $el.boolData( key )
 * < Boolean
 *
 * Setter
 * > $el.boolData( key, bool )
 * < $el
 * > $el.boolData( { key: bool [, ... ] } )
 * < $el
 */
$.fn.boolData = function (key, bool) {
  // Handle object syntax for setting multiple data attributes
  if (_.isObject(key)) {
    const attrs = _.transform(key, (result, value, key) => {
      result[keyToDataAttr(key)] = value
    })
    return this.boolAttr(attrs)
  }
  // Single data attribute getter/setter
  const attr = keyToDataAttr(key)
  return this.boolAttr(attr, bool)
}

/**
 * toggleData
 * Works like $el.toggleClass: http://api.jquery.com/toggleClass/
 * Also works like $el.data() setter: http://api.jquery.com/data/
 *
 * Setter
 * > $el.toggleData( key [, state ])
 * < $el
 */
$.fn.toggleData = function (key, state) {
  // Single data attribute setter/toggle
  const attr = keyToDataAttr(key)
  return this.toggleAttr(attr, state)
}

// Private functions

// http://api.jquery.com/attr/#attr-attributes
function boolAttributesObject(obj) {
  // Iterate through keys and run setter on each
  return  _.reduce(obj, (result, value, key) => {
    // Use $.fn.boolAttr to wrap suprising behavior
    return result.boolAttr(key, !!value)
  }, this)
}

// http://api.jquery.com/val/
function boolValueAttribute(bool) {
  // Value Setter
  if (typeof bool !== undefined) {
    return this.val(!!bool)
  }
  // Value Getter
  return this.val() === 'true'
}

// http://api.jquery.com/prop/
function boolProperty(attr, bool) {
  // Property Setter
  if (typeof bool !== 'undefined') {
    return this.prop(attr, !!bool)
  }
  // Property Getter
  return !!this.prop(attr)
}

// http://api.jquery.com/attr/
function boolAttribute(attr, bool) {
  // Attribute Setter
  if (typeof bool !== 'undefined') {
    return bool ? this.attr(attr, '') : this.removeAttr(attr)
  }
  // Attribute Getter
  const value = this.attr(attr)
  return typeof value !== 'undefined' && value !== false
}

// Helper function to convert names into data attribute names
// http://api.jquery.com/data/#data-html5
// https://www.w3.org/TR/html5/dom.html#dom-htmlelement-dataset
// https://www.w3.org/TR/2016/REC-html51-20161101/dom.html#custom-data-attribute
function keyToDataAttr(key) {
  const prefix = _.startsWith(key, 'data-') ? '' : 'data-'
  return prefix + _.kebabCase(key)
}
